import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, map, Observable } from "rxjs";
import {
  CreateNewsViewModel,
  NewsCategoryViewModel,
  NewsNavbarViewModel,
  NewsPaginationDto,
  NewsSuperUserViewModel
} from "../core/model/DemoNews";
import { environment } from "src/environments/environment";
import { brdCatchError } from "../shared/message-utils";

@Injectable({
  providedIn: "root"
})
export class DemoNewsService {
  private demoNewsSubject = new BehaviorSubject<NewsSuperUserViewModel[]>([]);
  demoNews$ = this.demoNewsSubject.asObservable();

  private newsCategoriesSubject = new BehaviorSubject<NewsCategoryViewModel[]>([]);
  newsCategories$ = this.newsCategoriesSubject.asObservable();

  private newsToReadSubject = new BehaviorSubject<NewsNavbarViewModel[]>([]);
  newsToRead$ = this.newsToReadSubject.asObservable();

  controllerName = "DemoNews";
  private apiUrl: string = `${environment.API}/${this.controllerName}`;

  constructor(private readonly httpClient: HttpClient) {}

  getAllNews(): Observable<NewsSuperUserViewModel[]> {
    return this.httpClient.get<NewsSuperUserViewModel[]>(`${this.apiUrl}/news`).pipe(
      map(res => {
        this.demoNewsSubject.next(res);
        return res;
      })
    );
  }

  getNews(id: number): Observable<CreateNewsViewModel> {
    return this.httpClient.get<CreateNewsViewModel>(`${this.apiUrl}/news/${id}`);
  }

  getNewsPopup(id: number): Observable<CreateNewsViewModel> {
    return this.httpClient.get<CreateNewsViewModel>(`${this.apiUrl}/news/${id}/popup`);
  }

  getNewsCategories(): Observable<NewsCategoryViewModel[]> {
    return this.httpClient.get<NewsCategoryViewModel[]>(`${this.apiUrl}/categories`).pipe(
      map(res => {
        this.newsCategoriesSubject.next(res);
        return res;
      })
    );
  }

  getNewsPagination(
    take: number,
    skip: number,
    filterText?: string,
    categoriesId?: number[],
    relatedAssets?: number[]
  ): Observable<NewsPaginationDto> {
    let url = `/news/pagination?take=${take}&skip=${skip}`;
    if (filterText) url = url.concat(`&filterText=${filterText}`);
    if (categoriesId) url = url.concat(`&categoriesId=${categoriesId.join(",")}`);
    if (relatedAssets) url = url.concat(`&relatedAssets=${relatedAssets.join(",")}`);
    return this.getNewsPaginationByUrl(url);
  }

  getNewsPaginationByUrl(url: string): Observable<NewsPaginationDto> {
    return this.httpClient.get<NewsPaginationDto>(`${this.apiUrl}${url}`);
  }

  addOrUpdate(news: CreateNewsViewModel): Observable<NewsSuperUserViewModel> {
    return this.httpClient.post<NewsSuperUserViewModel>(`${this.apiUrl}/news`, news).pipe(
      brdCatchError(),
      map(res => {
        const currentNews = [...this.demoNewsSubject.value];
        const newsIndex = currentNews.findIndex(x => x.id === res.id);
        if (newsIndex > -1) currentNews[newsIndex] = res;
        else currentNews.push(res);

        this.demoNewsSubject.next(currentNews);
        return res;
      })
    );
  }

  deleteNews(id: number): Observable<NewsSuperUserViewModel> {
    return this.httpClient.delete<NewsSuperUserViewModel>(`${this.apiUrl}/news/${id}`).pipe(
      brdCatchError(),
      map(res => {
        let currentNews = [...this.demoNewsSubject.value];
        currentNews = currentNews.filter(x => x.id !== id);

        this.demoNewsSubject.next(currentNews);
        return res;
      })
    );
  }

  deleteCategory(id: number): Observable<NewsCategoryViewModel> {
    return this.httpClient.delete<NewsCategoryViewModel>(`${this.apiUrl}/categories/${id}`).pipe(
      brdCatchError(),
      map(res => {
        let currentCategories = [...this.newsCategoriesSubject.value];
        currentCategories = currentCategories.filter(x => x.id !== id);

        this.newsCategoriesSubject.next(currentCategories);
        return res;
      })
    );
  }

  addCategory(category: NewsCategoryViewModel): Observable<NewsCategoryViewModel> {
    return this.httpClient.post<NewsCategoryViewModel>(`${this.apiUrl}/categories`, category).pipe(
      brdCatchError(),
      map(res => {
        this.newsCategoriesSubject.next([...this.newsCategoriesSubject.value, res]);
        return res;
      })
    );
  }

  updateCategory(id: number, category: NewsCategoryViewModel): Observable<NewsCategoryViewModel> {
    return this.httpClient.put<NewsCategoryViewModel>(`${this.apiUrl}/categories/${id}`, category).pipe(
      brdCatchError(),
      map(res => {
        const currentCategories = [...this.newsCategoriesSubject.value];
        const categoryIndex = currentCategories.findIndex(x => x.id === res.id);
        currentCategories[categoryIndex] = res;

        this.newsCategoriesSubject.next(currentCategories);
        return res;
      })
    );
  }

  getNewsToRead(): Observable<NewsNavbarViewModel[]> {
    return this.httpClient.get<NewsNavbarViewModel[]>(`${this.apiUrl}/news/toread`).pipe(
      map(res => {
        this.newsToReadSubject.next(res);
        return res;
      })
    );
  }

  updateUserLastRead(): Observable<void> {
    return this.httpClient.post<void>(`${this.apiUrl}/news/toread`, {}).pipe(
      map(res => {
        this.newsToReadSubject.next([]);
        return res;
      })
    );
  }
}
